import * as React from "react";
import { createPortal } from "react-dom";
import { Plus, X } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { useI18n } from "../../lib/i18n";
import * as styles from "./ModuleDetailPage.css";

const CLICK_THRESHOLD_PX = 6;
const ENTER_MS = 300;

type DragGhost = {
  number: number;
  w: number;
  h: number;
};

export function CourseQuestionsStrip({
  questionIds,
  selectedId,
  onSelect,
  onReorder,
  onAdd,
  onDelete,
  unsavedIds,
  disabled = false,
}: {
  questionIds: string[];
  selectedId: string | null;
  onSelect: (questionId: string) => void;
  onReorder: (orderedIds: string[]) => void;
  onAdd: () => void;
  onDelete: (questionId: string) => void;
  /** Question ids with unsaved edits (shows a small badge). */
  unsavedIds?: Set<string> | ReadonlySet<string>;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = React.useState(false);
  const [showRightFade, setShowRightFade] = React.useState(false);

  const idsRef = React.useRef(questionIds);
  idsRef.current = questionIds;

  const [dragSrcIdx, setDragSrcIdx] = React.useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
  const dragOverIdxRef = React.useRef<number | null>(null);
  dragOverIdxRef.current = dragOverIdx;

  const [dragGhost, setDragGhost] = React.useState<DragGhost | null>(null);
  const [dragGhostPos, setDragGhostPos] = React.useState({ x: 0, y: 0 });
  const dragGrabOffset = React.useRef({ x: 0, y: 0 });
  const pointerStart = React.useRef<{ x: number; y: number } | null>(null);
  const didDragRef = React.useRef(false);
  const [pointerTracking, setPointerTracking] = React.useState(false);
  const pendingDragRef = React.useRef<{ idx: number; ghost: DragGhost } | null>(null);
  const [enteringIds, setEnteringIds] = React.useState<Set<string>>(new Set());

  const updateFades = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftFade(scrollLeft > 4);
    setShowRightFade(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  React.useLayoutEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFades, questionIds.length]);

  React.useEffect(() => {
    if (!pointerTracking) return;

    const onPointerMove = (e: PointerEvent) => {
      const start = pointerStart.current;
      const pending = pendingDragRef.current;

      if (start && pending && !didDragRef.current) {
        const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        if (dist < CLICK_THRESHOLD_PX) return;

        didDragRef.current = true;
        document.documentElement.style.setProperty("cursor", "grabbing", "important");
        setDragSrcIdx(pending.idx);
        setDragGhost(pending.ghost);
        setDragGhostPos({ x: e.clientX, y: e.clientY });
      }

      if (!didDragRef.current) return;

      setDragGhostPos({ x: e.clientX, y: e.clientY });
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const circleEl = el?.closest("[data-question-drag-idx]") as HTMLElement | null;
      const over = circleEl ? parseInt(circleEl.dataset.questionDragIdx ?? "-1", 10) : -1;
      setDragOverIdx(over >= 0 ? over : null);
    };

    const onPointerUp = () => {
      document.documentElement.style.removeProperty("cursor");
      const src = pendingDragRef.current?.idx ?? null;
      const over = dragOverIdxRef.current;
      const moved = didDragRef.current;

      setPointerTracking(false);
      pendingDragRef.current = null;
      setDragSrcIdx(null);
      setDragOverIdx(null);
      setDragGhost(null);
      pointerStart.current = null;
      didDragRef.current = false;

      if (moved && src !== null && over !== null && src !== over) {
        const next = [...idsRef.current];
        const [item] = next.splice(src, 1);
        next.splice(over, 0, item);
        onReorder(next);
        requestAnimationFrame(() => {
          const lo = Math.min(src, over);
          const hi = Math.max(src, over);
          const movedIds = next.slice(lo, hi + 1);
          setEnteringIds((p) => {
            const set = new Set(p);
            movedIds.forEach((id) => set.add(id));
            return set;
          });
          window.setTimeout(() => {
            setEnteringIds((p) => {
              const set = new Set(p);
              movedIds.forEach((id) => set.delete(id));
              return set;
            });
          }, ENTER_MS);
        });
      } else if (!moved && src !== null) {
        const id = idsRef.current[src];
        if (id) onSelect(id);
      }
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [pointerTracking, onReorder, onSelect]);

  const handleCirclePointerDown = (idx: number) => (e: React.PointerEvent) => {
    if (disabled || e.button !== 0) return;
    e.preventDefault();

    const id = questionIds[idx];
    if (!id) return;

    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);

    dragGrabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    pointerStart.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;

    pendingDragRef.current = {
      idx,
      ghost: { number: idx + 1, w, h },
    };
    setPointerTracking(true);
  };

  const isDragActive = dragSrcIdx !== null;

  return (
    <div className={styles.questionsBlock}>
      <div className={styles.questionsHeader}>
        <p className={styles.fieldLabel}>
          {t("pro.system.certification.training.questions.title")}
        </p>
        <Button
          type="button"
          color="primary"
          variant="outline"
          size="sm"
          iconLeft={<Plus size={14} />}
          onClick={onAdd}
          disabled={disabled || isDragActive}
        >
          {t("pro.system.certification.training.questions.add")}
        </Button>
      </div>

      <div className={styles.questionsGalleryWrap}>
        <div
          ref={scrollRef}
          className={styles.questionsGalleryScroll}
          onScroll={updateFades}
        >
          <div
            className={[
              styles.questionsRow,
              isDragActive ? styles.questionsRowDragging : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {questionIds.map((id, index) => {
              const selected = selectedId === id;
              const isDragSrc = dragSrcIdx === index;
              const isDropTarget =
                dragOverIdx === index && dragSrcIdx !== null && dragSrcIdx !== index;
              const isSrcHover = dragOverIdx === index && dragSrcIdx === index;
              const isEntering = enteringIds.has(id);
              const hasUnsaved = unsavedIds?.has(id) ?? false;
              const number = index + 1;

              return (
                <div
                  key={id}
                  role="button"
                  tabIndex={0}
                  data-question-drag-idx={index}
                  className={[
                    styles.questionCircle,
                    selected ? styles.questionCircleSelected : "",
                    isDragSrc ? styles.questionCircleDragging : "",
                    isSrcHover ? styles.questionCircleDragSrcHover : "",
                    isDropTarget ? styles.questionCircleDropTarget : "",
                    isEntering ? styles.questionCircleEnter : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onPointerDown={handleCirclePointerDown(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(id);
                    }
                  }}
                  title={t("pro.system.certification.training.questions.questionN", {
                    n: number,
                  })}
                  aria-pressed={selected}
                  aria-disabled={disabled || undefined}
                >
                  <span className={styles.questionCircleNumber}>{number}</span>
                  {hasUnsaved && !isDragSrc ? (
                    <span className={styles.questionCircleUnsaved} aria-hidden />
                  ) : null}
                  {!isDragSrc ? (
                    <button
                      type="button"
                      className={styles.questionCircleDelete}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDelete(id);
                      }}
                      aria-label={t(
                        "pro.system.certification.training.questions.removeQuestion",
                      )}
                      title={t(
                        "pro.system.certification.training.questions.removeQuestion",
                      )}
                      disabled={disabled}
                    >
                      <X size={9} strokeWidth={3} aria-hidden />
                    </button>
                  ) : null}
                  {selected && !isDragSrc ? (
                    <span className={styles.questionCircleCaret} aria-hidden />
                  ) : null}
                </div>
              );
            })}

            <button
              type="button"
              className={styles.questionCircleAdd}
              onClick={onAdd}
              aria-label={t("pro.system.certification.training.questions.add")}
              title={t("pro.system.certification.training.questions.add")}
              disabled={disabled || isDragActive}
            >
              <Plus size={16} aria-hidden />
            </button>
          </div>
        </div>
        <div
          className={styles.questionsFadeLeft}
          style={{ opacity: showLeftFade ? 1 : 0 }}
          aria-hidden
        />
        <div
          className={styles.questionsFadeRight}
          style={{ opacity: showRightFade ? 1 : 0 }}
          aria-hidden
        />
      </div>

      {dragGhost
        ? createPortal(
            <div
              className={styles.questionDragGhost}
              style={{
                left: dragGhostPos.x - dragGrabOffset.current.x,
                top: dragGhostPos.y - dragGrabOffset.current.y,
                width: dragGhost.w,
                height: dragGhost.h,
                transformOrigin: `${dragGrabOffset.current.x}px ${dragGrabOffset.current.y}px`,
              }}
            >
              <span className={styles.questionCircleNumber}>{dragGhost.number}</span>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
